import { type JSX, useState } from 'react';
import { abbreviate } from '@chainflip/utils/string';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { Link } from '@/shared/components/atoms/Link';
import { SkeletonLine } from '@/shared/components/atoms/LoadingSkeleton';
import { ValidatorBadges } from '@/shared/components/atoms/ValidatorBadges';
import { ValidatorOnlineStatusIndicator } from '@/shared/components/atoms/ValidatorOnlineStatusIndicator';
import Button from '@/shared/components/flip-ui-kit/Button';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { FlipLabel } from '@/shared/components/molecules/FlipLabel';
import { Tooltip } from '@/shared/components/molecules/Tooltip';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import { type CastCacheValidatorFragment } from '@/shared/graphql/cache/fragments';
import useAuction from '@/shared/hooks/useAuction';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useOldFundingSDK from '@/shared/hooks/useOldFundingSdk';
import useScreenSize from '@/shared/hooks/useScreenSize';
import useTracking from '@/shared/hooks/useTracking';
import ArrowIcon from '@/shared/icons/flip-ui-kit/large/Arrow';
import MinusCircleIcon from '@/shared/icons/flip-ui-kit/large/MinusCircle';
import PlusCircleIcon from '@/shared/icons/flip-ui-kit/large/PlusCircle';
import WindowIcon from '@/shared/icons/flip-ui-kit/small/Window';
import { PENDING_REDEMPTION } from '@/shared/queryKeys';
import { FLIP_SYMBOL } from '@/shared/utils/env';
import { formatBps } from '@/shared/utils/strings';
import type TokenAmount from '@/shared/utils/TokenAmount';
import { MAX_REPUTATION_POINTS, POLL_INTERVAL } from 'packages/stake/utils/consts';
import { AddFundsModal, type AddFundsModalProps } from './AddFundsModal';
import { DiffLabel } from './DiffLabel';
import RedeemFundsModal from './RedeemFlow/RedeemFundsModal';
import { getValidatorCfeVersionQuery } from '../../../graphql/explorer/validatorQueries';
import { StakeEvents, type StakeTrackEvents } from '../../../types/track';

interface ValidatorCardProps {
  validator: Omit<CastCacheValidatorFragment, 'totalBalance'> & {
    totalBalance: TokenAmount;
    unregistered: boolean;
  };
  rewards: TokenAmount; // from processor db
  slashed: TokenAmount; // from processor db
  minActiveBid: TokenAmount;
}

const Row = ({ tooltip, label, value }: { tooltip: string; label: string; value: JSX.Element }) => (
  <div className="flex flex-col space-y-2">
    <div className="flex items-center">
      <QuestionMarkTooltip content={tooltip} />
      <span className="ml-2 text-12 text-cf-light-2">{label}</span>
      <div className="ml-auto">{value}</div>
    </div>
  </div>
);

const ValidatorCard: React.FC<ValidatorCardProps> = ({
  validator,
  rewards,
  slashed,
  minActiveBid,
}) => {
  const track = useTracking<StakeTrackEvents>();
  const [isRedeemModalActive, setIsRedeemModalActive] = useState(false);
  const { auction } = useAuction();
  const { fundingSDK } = useOldFundingSDK();
  const distanceFromMab = validator.totalBalance.sub(minActiveBid).abs();

  const { data: hasPendingRedemption } = useQuery({
    queryKey: [PENDING_REDEMPTION, validator.idHex, Boolean(fundingSDK)],
    queryFn: async () =>
      (await fundingSDK?.getPendingRedemption(validator.idHex as `0x${string}`)) ?? null,
    select: Boolean,
    refetchInterval: POLL_INTERVAL,
  });

  const { data: validatorCfeVersionData, isLoading: validatorCfeVersionLoading } = useGqlQuery(
    getValidatorCfeVersionQuery,
    {
      variables: { idSs58: validator.idSs58 },
      context: { clientName: 'processor' },
      refetchInterval: POLL_INTERVAL,
    },
  );
  const cfeVersion = validatorCfeVersionData?.account?.validator?.cfeVersionId;

  // the redemption button should be enabled
  const canRedeem =
    // if there is already a pending redemption
    hasPendingRedemption ||
    // or we are within the redemption period of an auction
    (auction?.isRedemptionPeriod ?? true) ||
    // or a validator is not bidding
    !validator.isBidding;

  // store active state and values in separate variables to prevent changes in the modal during closing animation
  const [addFundsModalActive, setAddFundsModalActive] = useState(false);
  const [addFundsModalValues, setAddFundsModalValues] = useState<
    AddFundsModalProps['nodeValues'] | null
  >(null);

  const { isMobile } = useScreenSize();

  const addFunds = () => {
    track(StakeEvents.AddFunds, {
      props: {
        scAccount: validator.idSs58,
        path: window.location.pathname,
        position: 'node_list_item',
      },
    });
    setAddFundsModalValues({
      nodeId: validator.idSs58,
      nodePublicKey: validator.idSs58,
      nodeCurrentBalance: validator.totalBalance,
    });
    setAddFundsModalActive(true);
  };

  if (validator.unregistered && !hasPendingRedemption) return null;

  return (
    <>
      <div
        className={classNames(
          'flex w-full flex-col rounded-md border border-cf-gray-4',
          hasPendingRedemption ? 'bg-holy-radial-pending-redemption' : 'bg-holy-radial-6',
        )}
      >
        <div className="flex w-full items-center border-b border-cf-gray-4 p-4">
          <div className="flex w-full flex-col space-y-2">
            <div className="flex w-full flex-col items-start gap-y-2 sm:flex-row sm:justify-between sm:gap-y-0 sm:space-x-4">
              <div className="order-2 flex flex-row space-x-2 sm:order-none">
                <span className="text-20 text-white">{validator.alias || 'Unnamed'}</span>
                <ValidatorOnlineStatusIndicator isOnline={validator.isOnline} />
              </div>
              <div className="order-1 flex space-x-2 sm:order-none sm:justify-end">
                <ValidatorBadges cacheValidator={validator} />
              </div>
            </div>
            <div className="w-full flex-col items-center justify-between gap-y-2 sm:flex sm:flex-row sm:gap-x-4 sm:gap-y-0">
              <div className="order-2 flex flex-row items-center gap-x-2 sm:order-none">
                <div className="text-14 text-cf-light-2">
                  {abbreviate(validator.idSs58, isMobile ? 4 : 8)}
                </div>
                <CopyButton textToCopy={validator.idSs58} />
                <Link
                  href={`${process.env.NEXT_PUBLIC_EXPLORER_URL}/validators/${validator.idSs58}`}
                  target="_blank"
                >
                  <div className="rounded-md border border-cf-gray-5 bg-cf-gray-4 p-1 hover:bg-cf-gray-5">
                    <WindowIcon />
                  </div>
                </Link>
              </div>
              <div className="order-1 flex items-center space-x-1 font-aeonikMedium text-12 sm:order-none sm:justify-end">
                <span className="text-cf-light-2">CFE version</span>
                <span className="text-cf-white">
                  {validatorCfeVersionLoading ? <SkeletonLine count={1} width={25} /> : cfeVersion}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col space-y-4 p-4">
          <div className="flex flex-col items-start space-y-3 sm:flex-row sm:items-center sm:space-y-0">
            <div className="flex flex-col space-y-2">
              <span className="text-14 text-cf-light-2">Balance</span>
              <FlipLabel amount={validator.totalBalance} large hideSymbol />
            </div>
            <div className="flex items-center space-x-2 sm:ml-auto">
              <Button
                size="small"
                type="secondary-standard"
                className="space-x-1"
                onClick={addFunds}
              >
                <PlusCircleIcon className="text-cf-green-1" />
                <span>Add</span>
              </Button>
              <Tooltip
                disabled={canRedeem}
                content={`Bidding validators can't redeem their ${FLIP_SYMBOL} balance until the current auction is resolved.`}
              >
                <Button
                  size="small"
                  type="secondary-standard"
                  className="space-x-1"
                  disabled={!canRedeem}
                  onClick={() => {
                    track(StakeEvents.RedeemFunds, {
                      props: {
                        scAccount: validator.idSs58,
                        path: window.location.pathname,
                        position: 'node_list_item',
                      },
                    });
                    setIsRedeemModalActive(true);
                  }}
                >
                  {!hasPendingRedemption && (
                    <MinusCircleIcon
                      className={classNames('text-cf-red-1', !canRedeem && 'opacity-30')}
                    />
                  )}
                  <span>{hasPendingRedemption ? 'Continue Redemption' : 'Redeem'}</span>
                  {hasPendingRedemption && (
                    <ArrowIcon className="text-cf-orange-1" direction="right" />
                  )}
                </Button>
              </Tooltip>
            </div>
          </div>
          <Row
            tooltip={`The amount of ${FLIP_SYMBOL} earned by this validator node.`}
            label="Lifetime Rewards"
            value={<FlipLabel amount={rewards} />}
          />
          <Row
            tooltip={`Total expected rewards over the course of a year divided by the total ${FLIP_SYMBOL} balance for this validator.`}
            label="Est. APY"
            value={
              <span className="text-12 text-cf-white">≈ {formatBps(validator.apyBp ?? 0)}</span>
            }
          />
          <Row
            tooltip={`The difference in ${FLIP_SYMBOL} between the balance of this validator node and the Minimum Active Bid (MAB).`}
            label="Distance from MAB"
            value={
              <div className="flex flex-col items-end space-x-1 sm:flex-row sm:items-center">
                {minActiveBid.value.isEqualTo(0) ? (
                  <span className="text-12 text-cf-white">-</span>
                ) : (
                  <>
                    <FlipLabel amount={distanceFromMab} />
                    <DiffLabel
                      className="text-12 "
                      percentage={validator.totalBalance.difference(minActiveBid)}
                    />
                  </>
                )}
              </div>
            }
          />
          <Row
            label="Available for redemption"
            tooltip={`The amount of ${FLIP_SYMBOL} that isn't bonded and available for redemption for this validator node.`}
            value={<FlipLabel amount={validator.unlockedBalance} />}
          />
          <Row
            tooltip={`The amount of confiscated funds in ${FLIP_SYMBOL} for this validator node due to failing to fulfil its duties. These funds are burned as a penalty`}
            label="Lifetime Slashing"
            value={<FlipLabel amount={slashed} />}
          />
          <Row
            tooltip="The points earned for performing validator node duties and staying online. If it drops below zero, this validator node might be slashed."
            label="Reputation"
            value={
              <div className="text-12 text-cf-white">
                {validator.reputationPoints} / {MAX_REPUTATION_POINTS}
              </div>
            }
          />
        </div>
      </div>
      <RedeemFundsModal
        active={isRedeemModalActive}
        validator={validator}
        onCancel={() => setIsRedeemModalActive(false)}
      />
      <AddFundsModal
        active={addFundsModalActive}
        nodeValues={addFundsModalValues}
        onClose={() => setAddFundsModalActive(false)}
      />
    </>
  );
};

export default ValidatorCard;
